var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import debounce from 'lodash.debounce';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import merge from 'lodash.merge';
import set from 'lodash.set';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import useWatch from '../../hooks/useWatch';
import { interpolate } from '../../utils/strings';
export var PreloadsContext = React.createContext({
    preloads: {},
    addPreloads: function (param, values) {
        /*allow empty*/
    },
});
var getBaseConfig = function (config) {
    if (!config)
        return;
    var ADMIN_API_URL = config.ADMIN_API_URL;
    return {
        url: ADMIN_API_URL,
        opts: {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        },
    };
};
function getFetchOptions(_a) {
    var baseApiConfig = _a.baseApiConfig, preload = _a.preload;
    var url = preload.url.match(/^http|^https|^www/)
        ? preload.url
        : baseApiConfig === null || baseApiConfig === void 0 ? void 0 : baseApiConfig.url.replace('[clientName]', preload.url);
    return merge({}, baseApiConfig, preload, { url: url });
}
function includeOptions(includes, config) {
    if (!includes)
        return {};
    return includes.reduce(function (acc, _a) {
        var attrName = _a[0], attrPathValue = _a[1];
        set(acc, attrName, get(config, attrPathValue, attrPathValue));
        return acc;
    }, {});
}
// TODO: Migrate this to a common api fetch
function fetchPreload(url, options, config) {
    var includes = options.includes, opts = __rest(options, ["includes"]);
    var fetchOptions = merge({}, opts, includeOptions(includes, config));
    return fetch(url, fetchOptions).then(function (res) {
        if (!res.ok) {
            throw new Error(res.statusText);
        }
        return res.json();
    }, function (e) {
        throw new Error(e);
    });
}
var initialState = {};
function reducer(state, _a) {
    var _b;
    var key = _a.key, value = _a.value;
    if (!key || !value)
        return state;
    return __assign(__assign({}, state), (_b = {}, _b[key] = value, _b));
}
export function removeHashKey(hashList, hashKey, keepNewHashKey) {
    if (keepNewHashKey === void 0) { keepNewHashKey = true; }
    var baseHashKey = hashKey.split('?')[0];
    var newList = hashList.filter(function (hash) {
        return (keepNewHashKey && (!hash.includes(baseHashKey) || hash === hashKey)) ||
            (!keepNewHashKey && hashKey !== hash);
    });
    return newList;
}
export function addHashKey(hashList, hashKey) {
    return hashList.concat(hashKey);
}
function prepareDynamicPreloads(requests) {
    if (requests === void 0) { requests = {}; }
    return Object.keys(requests).reduce(function (acc, key) {
        var preload = requests[key];
        if (preload.url.match(/{{.*}}/)) {
            acc[key] = preload;
        }
        return acc;
    }, {});
}
export var PreloadsProvider = function (_a) {
    var config = _a.config, _b = _a.requestOptionsHandler, requestOptionsHandler = _b === void 0 ? getFetchOptions : _b, _c = _a.preloads, watch = _c.watch, requests = _c.requests, _d = _c.debounceOpts, debounceOpts = _d === void 0 ? { duration: 1000 } : _d, children = _a.children;
    var _e = useReducer(reducer, initialState), state = _e[0], dispatch = _e[1];
    var watchedValues = useWatch(watch);
    var hashListRef = useRef([]);
    var dynamicPreloadsRef = useRef(prepareDynamicPreloads(requests));
    var debouncedRef = useRef(debounce(function (watchedPreloads, updatedValues) {
        return addPreloads(watchedPreloads, updatedValues);
    }, debounceOpts.duration, debounceOpts.opts));
    var preloadHandler = useCallback(function (_a, values) {
        var url = _a.url, opts = _a.opts, key = _a.key, _b = _a.dataAttrName, dataAttrName = _b === void 0 ? '' : _b, presenter = _a.presenter, presenterArgs = _a.presenterArgs;
        if (values === void 0) { values = {}; }
        var interpolatedUrl = interpolate(url, values);
        var hashKey = "".concat(key, ":").concat(interpolatedUrl);
        if (hashListRef.current.includes(hashKey))
            return;
        hashListRef.current = addHashKey(hashListRef.current, hashKey);
        fetchPreload(interpolatedUrl, opts, config)
            .then(function (res) {
            var value = res[dataAttrName] || res;
            dispatch({ key: key, value: (presenter === null || presenter === void 0 ? void 0 : presenter(value, presenterArgs)) || value });
            hashListRef.current = removeHashKey(hashListRef.current, hashKey);
        })
            .catch(function (e) {
            hashListRef.current = removeHashKey(hashListRef.current, hashKey, false);
            console.error("Something went wrong fetching preload ".concat(key, ". Be aware it was not set on preloads."), e);
        });
    }, [dispatch]);
    var addPreloads = useCallback(function (newPreloads, values) {
        Object.keys(newPreloads).forEach(function (key) {
            var preload = newPreloads[key];
            var _a = requestOptionsHandler({
                baseApiConfig: getBaseConfig(config),
                preload: preload,
            }), url = _a.url, opts = _a.opts, dataAttrName = _a.dataAttrName, _b = _a.presenterId, presenterId = _b === void 0 ? '' : _b, _c = _a.presenterArgs, presenterArgs = _c === void 0 ? {} : _c;
            if (!url) {
                console.error("Preload url for ".concat(key, " is missing. Be aware that options were not preloaded."));
                return;
            }
            preloadHandler({
                url: url,
                opts: opts,
                key: key,
                dataAttrName: dataAttrName,
                presenter: PreloadsProvider.presenters[presenterId],
                presenterArgs: presenterArgs,
            }, values);
        });
    }, []);
    useEffect(function () {
        if (!isEmpty(requests) && watchedValues) {
            addPreloads(requests, watchedValues);
        }
    }, [requests, dispatch, requestOptionsHandler]);
    useEffect(function () {
        var _a;
        if (watchedValues) {
            (_a = debouncedRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
            debouncedRef.current(dynamicPreloadsRef.current, watchedValues);
        }
    }, [watchedValues]);
    return (_jsx(PreloadsContext.Provider, __assign({ value: { preloads: state, addPreloads: addPreloads } }, { children: children })));
};
PreloadsProvider.presenters = {};
PreloadsProvider.registerPresenter = function (id, presenter) {
    PreloadsProvider.presenters[id] = presenter;
};
